import KRGlue from "@lyracom/embedded-form-glue";
import "flatpickr/dist/flatpickr.min.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Flatpickr from "react-flatpickr";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL, N8N_ORDER_CONFIRMATION_URL } from "../../constants";
import { serializeCartForOrder } from "../../utils/serializeCartForOrder";
import { computeOrderTotals } from "../../utils/orderPricing";
import { normalizeConfig } from "../../utils/normalizeConfig";
import { useConfig } from "../../config/ConfigContext";
import { useMeals } from "../Meal/MealsContext";
import BillingForm from "./BillingForm";
import OrderSummarySidebar from "./OrderSummarySidebar";
import PaymentModal from "./PaymentModal";

function Checkout() {
  const { cart, categorizedCart } = useMeals();
  const config = useConfig();
  const noCheckout = config.noCheckout || config.id === "versionNoCheckout";
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const baseUrl = BASE_URL;

  const [form, setForm] = useState({
    deliveryDate: null,
    latestDeliveryTime: null,
    boatName: null,
    googleMapsLink: null,
    location: null,
    recipientName: null,
    recipientPhone: null,
    deliveryNotes: null,
    numberOfPeople: null,
    dietarySpecifications: null,
    email: null,
    firstName: null,
    lastName: null,
    address1: null,
    postcode: null,
    city: null,
    country: null,
    companyName: null,
    boatModel: null,
    orderDate: null,
  });

  const formRef = useRef({});
  useEffect(() => {
    formRef.current = form;
  }, [form]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (noCheckout && location.state?.orderDate) {
      setForm((prev) => ({ ...prev, orderDate: location.state.orderDate }));
    }
  }, [noCheckout, location.state?.orderDate]);

  const handleChange = (value, fieldName = null) => {
    if (fieldName === "recipientPhone") {
      // Ensure value is defined before processing
      if (!value) {
        setForm((prevForm) => ({
          ...prevForm,
          recipientPhone: "",
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          recipientPhone: "Invalid phone number",
        }));
        return;
      }

      // Parse the phone number with default country FR
      try {
        const phoneNumber = parsePhoneNumberFromString(value, "FR");

        setForm((prevForm) => ({
          ...prevForm,
          recipientPhone: value,
        }));

        setErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          if (!phoneNumber || !phoneNumber.isValid()) {
            updatedErrors.recipientPhone = "Invalid phone number";
          } else {
            delete updatedErrors.recipientPhone;
          }
          return updatedErrors;
        });
      } catch (error) {
        console.error("Phone parsing error:", error);
        setErrors((prevErrors) => ({
          ...prevErrors,
          recipientPhone: "Invalid phone number",
        }));
      }
      return;
    }

    // Handle normal inputs
    const { name, value: inputValue } = value.target;

    setForm((prevForm) => ({
      ...prevForm,
      [name]: inputValue,
    }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (name === "numberOfPeople") {
        const num = Number(inputValue);
        if (!Number.isNaN(num) && num >= 1) {
          delete updatedErrors[name];
        }
      } else if (inputValue.trim() !== "") {
        delete updatedErrors[name];
      }
      return updatedErrors;
    });
  };

  const totalItemPriceTTC = cart.reduce(
    (sum, item) =>
      sum + item.quantity * item.meal.fields[config.itemPriceFieldTTC],
    0
  );
  const totalItemPriceHT = cart.reduce(
    (sum, item) =>
      sum + item.quantity * item.meal.fields[config.itemPriceFieldHT],
    0
  );
  const {
    deliveryFee,
    totalDiscount,
    totalPriceDisplay,
    totalPriceHT,
    partnerCommissionPercent,
  } = computeOrderTotals(config, totalItemPriceTTC, totalItemPriceHT);
  const cfg = normalizeConfig(config);

  const buildOrderSnapshot = useCallback(
    () => ({
      project: "catering-order",
      partnerId: config.id,
      formData: formRef.current,
      cart: serializeCartForOrder(cart, config),
      totals: {
        totalPriceDisplay,
        totalPriceHT,
        totalDiscount,
        deliveryFee,
        totalItemPriceTTC,
        partnerCommissionPercent,
      },
    }),
    [
      cart,
      config,
      totalPriceDisplay,
      totalPriceHT,
      totalDiscount,
      deliveryFee,
      totalItemPriceTTC,
      partnerCommissionPercent,
    ],
  );

  const autocompleteRef = useRef(null);

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    setForm({
      ...form,
      address1: place.formatted_address, // Depending on your needs you might want to parse street, city, zip separately
      city: place.address_components.find((c) => c.types.includes("locality"))
        ?.long_name,
      postcode: place.address_components.find((c) =>
        c.types.includes("postal_code")
      )?.long_name,
      country: place.address_components.find((c) => c.types.includes("country"))
        ?.long_name,
    });
  };
  const handlePaymentSubmit = useCallback(
    async (paymentData) => {
      try {
        const response = await fetch(
          `${baseUrl}/.netlify/functions/acceptPayment`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...paymentData,
              orderSnapshot: buildOrderSnapshot(),
            }),
          }
        );

        const paymentResult = await response.json();

        if (
          response.status === 200 &&
          paymentResult.message === "Valid payment"
        ) {
          const orderResponse = await fetch(N8N_ORDER_CONFIRMATION_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                formData: formRef.current, // ✅ Use latest form state from ref
                paymentId: paymentResult.transactionId ?? paymentData.transactionId,
                cart: serializeCartForOrder(cart, config), // ✅ Assuming cart is fixed
                config: config, // ✅ Assuming config is fixed
                totalPriceDisplay: totalPriceDisplay,
                totalPriceHT: totalPriceHT,
                totalDiscount: totalDiscount,
                deliveryFee: deliveryFee,
                totalItemPriceTTC: totalItemPriceTTC,
                partnerCommissionPercent,
              }),
          });

          const orderResponseData = await orderResponse.json();

          if (orderResponseData.success) {
            navigate("/confirmation", {
              state: {
                orderNumber: orderResponseData.orderNumber,
                firstName: formRef.current.firstName,
                lastName: formRef.current.lastName,
              },
            });

            console.log("✅ Order successfully stored");
          } else {
            console.error("⚠ Order storage failed");
          }
        } else {
          console.error("⚠ Payment validation failed");
        }
      } catch (error) {
        console.error("❌ Error processing payment:", error);
      }
    },
    [
      baseUrl,
      navigate,
      cart,
      config,
      totalPriceDisplay,
      totalPriceHT,
      totalDiscount,
      deliveryFee,
      totalItemPriceTTC,
      partnerCommissionPercent,
      buildOrderSnapshot,
    ]
  );

  useEffect(() => {
    if (noCheckout) return;

    async function setupPaymentForm() {
      try {
        const endpoint = "https://api.systempay.fr";
        const publicKey = process.env.REACT_APP_SYSTEM_PAY_PUBLIC_KEY;

        const response = await fetch(
          `${baseUrl}/.netlify/functions/createFormToken`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paymentConf: {
                amount: Math.round(totalPriceDisplay * 100),
                currency: "EUR",
              },
              orderSnapshot: buildOrderSnapshot(),
            }),
          }
        );

        const data = await response.json();

        const { KR } = await KRGlue.loadLibrary(endpoint, publicKey);

        await KR.setFormConfig({
          "kr-language": "en-US",
          formToken: data.formToken,
          "kr-popin": true,
        });
        await KR.removeForms();
        await KR.renderElements("#myPaymentForm");

        // Store form data and call API on submit
        KR.onSubmit(async (paymentData) => {
          await handlePaymentSubmit(paymentData);
        });
      } catch (error) {
        console.error("Payment form setup error:", error);
      }
    }

    setupPaymentForm();
  }, [noCheckout, baseUrl, handlePaymentSubmit, totalPriceDisplay, buildOrderSnapshot]);

  const getValidationErrors = () => {
    const requiredFields = config.requiredFields
      ? [...config.requiredFields]
      : [];

    if (!requiredFields.includes("numberOfPeople")) {
      requiredFields.push("numberOfPeople");
    }

    if (showBilling) {
      requiredFields.push(
        "companyName",
        "address1",
        "postcode",
        "city",
        "country"
      );
    }

    const newErrors = {};
    requiredFields.forEach((field) => {
      const value = form[field];

      if (field === "deliveryDate" && !(value instanceof Date)) {
        newErrors[field] = true; // Ensure deliveryDate is a valid Date object
      } else if (field === "numberOfPeople") {
        const num = Number(value);
        if (
          value === null ||
          value === undefined ||
          (typeof value === "string" && value.trim() === "") ||
          Number.isNaN(num) ||
          num < 1
        ) {
          newErrors[field] = true;
        }
      } else if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        newErrors[field] = true;
      }
    });

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = getValidationErrors();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors); // Mark fields in error
      return; // Prevent submission
    }

    if (noCheckout) {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const orderResponse = await fetch(N8N_ORDER_CONFIRMATION_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            formData: formRef.current,
            cart: serializeCartForOrder(cart, config),
            config,
            totalPriceDisplay,
            totalPriceHT,
            totalDiscount,
            deliveryFee,
            totalItemPriceTTC,
            partnerCommissionPercent,
          }),
        });

        const orderResponseData = await orderResponse.json();

        if (orderResponseData.success) {
          navigate("/confirmation", {
            state: {
              orderNumber: orderResponseData.orderNumber,
              firstName: formRef.current.firstName,
              lastName: formRef.current.lastName,
              noPayment: true,
            },
          });
        } else {
          setSubmitError("Order submission failed. Please try again.");
        }
      } catch (error) {
        console.error("Error submitting order:", error);
        setSubmitError("An error occurred. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsModalOpen(true);
    document.getElementById("myPaymentForm")?.classList.remove("hidden");

    document
      .querySelector(".kr-card-holder-mail input")
      ?.setAttribute("value", form.email);
  };

  const getInputClass = (field) => {
    return `input-style w-full ${errors[field] ? "input-error" : ""}`;
  };
  return (
    <div className=" max-w-6xl mx-auto p-6">
      <div className="mb-14">
        <h2 className="text-2xl font-semibold ">
          <span onClick={() => navigate("/")} className="cursor-pointer">
            &lt;
          </span>{" "}
          {noCheckout ? "Confirm order" : "Confirm and pay"}
        </h2>
      </div>
      <div className="flex flex-col-reverse md:grid md:grid-cols-2  md:gap-14">
        <form
          onSubmit={handleSubmit}
          noValidate
          className="space-y-4 form-section"
        >
          <h3 className="text-xl font-medium mt-10 md:mt-0">
            Delivery Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
            <Flatpickr
              value={form.deliveryDate}
              onChange={(date) => {
                if (date.length > 0) {
                  const adjustedDate = new Date(
                    date[0].getTime() + 60 * 60 * 1000
                  ); // Adjust to UTC+1
                  setForm((prevForm) => ({
                    ...prevForm,
                    deliveryDate: adjustedDate,
                  }));
                }
              }}
              options={{
                dateFormat: "d/m/Y",
                minDate: new Date().fp_incr(1),
                enableTime: false,
                disableMobile: true,
              }}
              className={`${getInputClass(
                "deliveryDate"
              )} col-span-2 md:col-span-1`}
              placeholder="Delivery Date*"
            />

            {config.id === "version1" && (
              <>
                {!showTimePicker && form.latestDeliveryTime === null ? (
                  <input
                    className="input-style col-span-2 md:col-span-1"
                    type="text"
                    name="latestDeliveryTime"
                    placeholder="Latest Delivery Time"
                    value=""
                    onMouseEnter={() => setShowTimePicker(true)}
                  />
                ) : (
                  <input
                    className="input-style col-span-2 md:col-span-1"
                    type="time"
                    name="latestDeliveryTime"
                    placeholder="Latest Delivery Time"
                    value={form.latestDeliveryTime ?? ""}
                    onChange={handleChange}
                    onMouseLeave={() => setShowTimePicker(false)}
                  />
                )}
              </>
            )}
            <input
              className="input-style col-span-2 md:col-span-1"
              type="text"
              name="boatName"
              placeholder="Boat Name"
              value={form.boatName ?? ""}
              onChange={handleChange}
            />
            {config.id === "version1" && (
              <>
                <input
                  className="input-style col-span-2 md:col-span-1"
                  type="text"
                  name="boatModel"
                  placeholder="Boat Model"
                  value={form.boatModel ?? ""}
                  onChange={handleChange}
                />
                <input
                  className="input-style col-span-2 md:col-span-1"
                  type="text"
                  name="recipientName"
                  placeholder="Recipient Name"
                  value={form.recipientName ?? ""}
                  onChange={handleChange}
                />
                {!showPhone && form.recipientPhone === null ? (
                  <input
                    className="input-style col-span-2 md:col-span-1"
                    type="text"
                    name="recipientPhone"
                    placeholder="Recipient Phone"
                    value=""
                    onMouseEnter={() => setShowPhone(true)}
                  />
                ) : (
                  <div className="col-span-2 md:col-span-1">
                    <PhoneInput
                      className={`input-style ${
                        errors.recipientPhone ? "input-error" : ""
                      }`}
                      placeholder="Recipient Phone"
                      value={form.recipientPhone ?? ""}
                      onChange={(value) =>
                        handleChange(value, "recipientPhone")
                      }
                      onMouseLeave={() => setShowPhone(false)}
                      defaultCountry="FR"
                      international
                    />
                    {errors.recipientPhone && (
                      <p className="text-red-500 text-sm">
                        {errors.recipientPhone}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            {config.id !== "version1" && (
              <>
                <input
                  className="input-style col-span-2 md:col-span-1"
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={form.firstName ?? ""}
                  onChange={handleChange}
                />
                <input
                  className={`${getInputClass(
                    "lastName"
                  )} col-span-2 md:col-span-1`}
                  type="text"
                  name="lastName"
                  placeholder="Last Name*"
                  value={form.lastName ?? ""}
                  onChange={handleChange}
                />
                <input
                  className={`${getInputClass(
                    "email"
                  )} col-span-2 md:col-span-1`}
                  type="email"
                  name="email"
                  placeholder="Email*"
                  value={form.email ?? ""}
                  onChange={handleChange}
                />
                <input
                  className={`${getInputClass(
                    "numberOfPeople"
                  )} col-span-2 md:col-span-1`}
                  type="number"
                  name="numberOfPeople"
                  placeholder="Number of People*"
                  value={form.numberOfPeople ?? ""}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </>
            )}
            {config.id === "version1" && (
              <input
                className="input-style col-span-2"
                type="text"
                name="googleMapsLink"
                placeholder="Google Maps Link"
                value={form.googleMapsLink ?? ""}
                onChange={handleChange}
              />
            )}
            <textarea
              className="input-style col-span-2"
              name="deliveryNotes"
              value={form.deliveryNotes ?? ""}
              placeholder="Notes"
              onChange={handleChange}
            />
          </div>
          {config.id === "version1" && (
            <>
              <h3 className="text-xl font-medium pt-3">Order Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
                <input
                  className={`${getInputClass(
                    "email"
                  )} col-span-2 md:col-span-1`}
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={form.email ?? ""}
                  onChange={handleChange}
                />
                <input
                  className={`${getInputClass(
                    "numberOfPeople"
                  )} col-span-2 md:col-span-1`}
                  type="number"
                  name="numberOfPeople"
                  placeholder="Number of People*"
                  value={form.numberOfPeople ?? ""}
                  onChange={handleChange}
                  min="1"
                  required
                />
                <textarea
                  className="input-style col-span-2"
                  name="dietarySpecifications"
                  value={form.dietarySpecifications ?? ""}
                  placeholder="Dietary Specifications"
                  onChange={handleChange}
                />
              </div>
            </>
          )}
          {config.id !== "version1" && (
            <BillingForm
              form={form}
              handleChange={handleChange}
              handlePlaceChanged={handlePlaceChanged}
              showBilling={showBilling}
              setShowBilling={setShowBilling}
              getInputClass={getInputClass}
              autocompleteRef={autocompleteRef}
            />
          )}
          {Object.keys(errors).length > 0 && (
            <p className="text-sm text-red-500">
              Please fill in all required fields.
            </p>
          )}
          {submitError && (
            <p className="text-sm text-red-500">{submitError}</p>
          )}
          <button
            type="submit"
            disabled={noCheckout && isSubmitting}
            className="bg-[#0e3141]  text-white  font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {noCheckout
              ? isSubmitting
                ? "Submitting..."
                : "Submit"
              : "Confirm and Pay"}
          </button>
          <div className="pt-4 text-sm text-center md:hidden">
            {noCheckout
              ? "Your order will be validated shortly."
              : "You will receive a confirmation email as soon as your payment is processed."}
          </div>
        </form>
        <OrderSummarySidebar
          categorizedCart={categorizedCart}
          cart={cart}
          config={config}
          deliveryFee={deliveryFee}
          totalDiscount={totalDiscount}
          totalPriceDisplay={totalPriceDisplay}
          totalPriceHT={totalPriceHT}
          noCheckout={noCheckout}
          showDeliveryFee={cfg.showDeliveryFeeInSummary}
          showPartnerDiscount={cfg.showPartnerDiscountInSummary}
        />
      </div>

      {!noCheckout && (
        <PaymentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Payment Information"
        >
          <div id="myPaymentForm" className={isModalOpen ? "" : "hidden"}>
            <div className="kr-smart-form">
              <div className="kr-embedded">
                <div className="kr-cardholder-name"></div>
                <div className="kr-pan"></div>
                <div className="kr-security-code w-1/2"></div>
                <div className="kr-expiry w-1/2"></div>
              </div>
            </div>
          </div>
        </PaymentModal>
      )}
    </div>
  );
}

export default Checkout;
